/**
 * Search-param (de)serialization for the router.
 *
 * TanStack's default parser turns `tenant=1234567890` into a number and
 * JSON-parses anything that looks like JSON, so hand-written and legacy links
 * with plain tenant ids fail `z.string()` validation. Every value here stays a
 * string; routes coerce numbers explicitly (`z.coerce.number()`).
 *
 * Links written by the old default serializer quoted string values that looked
 * like JSON (`tenant=%221234567890%22`); those are unquoted for compatibility.
 */
export function parseSearch(searchStr: string): Record<string, string> {
  const params = new URLSearchParams(searchStr.startsWith("?") ? searchStr.slice(1) : searchStr);
  const out: Record<string, string> = {};
  for (const [key, raw] of params.entries()) {
    if (key in out) continue;
    out[key] = unquote(raw);
  }
  return out;
}

function unquote(value: string): string {
  if (value.length >= 2 && value.startsWith('"') && value.endsWith('"')) {
    try {
      const parsed: unknown = JSON.parse(value);
      if (typeof parsed === "string") return parsed;
    } catch {
      /* not a quoted JSON string; keep as-is */
    }
  }
  return value;
}

export function stringifySearch(search: Record<string, unknown>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(search)) {
    if (value === undefined || value === null) continue;
    params.set(key, typeof value === "object" ? JSON.stringify(value) : String(value));
  }
  const str = params.toString();
  return str ? `?${str}` : "";
}
